/* Copyright (c) 2020-present CloudIO Inc */

import { infoBoxShowOn } from 'componentregistry/registry/constants';
import { getName } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './InfoBox';

const name = 'Info Box';
const InfoBoxManager: ComponentManagerFn<'InfoBox'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'InfoBox'>(platform, 'InfoBox'),
    type: 'InfoBox',
    name: 'Info Box',
    isChildAllowed: (page, item, childType, { pageId, appUid }) => {
      if (
        !(
          [
            'Box',
            'Card',
            'FlexPanel',
            'Paper',
            'TriggerOnPopupOpen',
            'RowPanel',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (childType === 'Card') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        if (children.length > 0)
          return `${getName(childType)} already added to ${name}`;
      }
      return true;
    },
    defaultValue,
    props: {
      showOn: {
        label: 'Show On',
        type: 'select',
        options: infoBoxShowOn,
      },
      infoBoxHeight: {
        label: 'Height',
        type: 'number',
        min: 100,
        max: 800,
        step: 40,
      },
      infoBoxWidth: {
        label: 'Width',
        type: 'number',
        min: 200,
        max: 1200,
        step: 40,
      },
      borderRadius: {
        label: 'Border Radius',
        type: 'number',
        min: 0,
        max: 32,
        step: 4,
        category: 'Look & Feel',
      },
    },
    getDisplayText: (item) => '',
    category: 'Containers',
    layout: 'sizeAware',
    icon: { iconName: 'comment-alt' },
    description: `**Overview**<br/>
The Info Box is a lightweight, ephemeral container designed to display contextual information on demand. Commonly known as a "popper" or "tooltip," this panel appears dynamically—typically in response to a user hovering over a specific element like a map marker or icon—and disappears automatically when the user interacts elsewhere.
Use the Info Box to declutter your interface by hiding auxiliary details until the user explicitly requests them.

**1. Trigger Behavior**<br/>
Because the Info Box is not permanently visible, you must define the interaction that reveals it.
* **Show On:** Determines the event that triggers the popup.
* **Hover:** The box appears when the cursor rests over the target element (standard for tooltips).
* **Click:** The box appears when the user clicks the target (standard for popovers or small menus).

**2. Sizing & Dimensions**<br/>
Unlike standard layout containers that expand to fill available space, the Info Box requires explicit boundaries to ensure it does not obstruct critical content when it appears.
* **Height:** Sets the vertical size of the floating panel (Min: 100px, Max: 800px).
* **Width:** Sets the horizontal size of the floating panel (Min: 200px, Max: 1200px).

**3. Visual Styling**<br/>
* **Border Radius:** Controls the curvature of the box's corners. (0 creates a sharp, rectangular look; higher values like 8-16 create a softer, card-like appearance).

**4. Content Composition**<br/>
The Info Box serves as a wrapper for your actual content components. While it is small, it is robust enough to hold structured layouts.
* **Allowed Content:** You can place Layouts (FlexPanel, RowPanel), Visuals (Card, Paper), or Logic (TriggerOnPopupOpen) inside an Info Box.
* **Constraint:** To prevent layout complexity in such a small space, you are limited to a single Card component as a child.`,
  };
};

export default InfoBoxManager;
